import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { NO_FEATURES, type FeatureFlags, type Me } from '@greencandle/api-client'
import { api, onSignedOut } from './api'
import { router } from './routes'

export interface AuthState {
  user: Me | null
  /**
   * What this installation has switched on, off until the session is known.
   *
   * <p>It rides on the user rather than being fetched separately: `/api/auth/me` carries it, so
   * there is one request, one loading state and no second race for the router to lose.
   */
  features: FeatureFlags
  /** True until the stored token has been checked against the server on first load. */
  loading: boolean
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  // A stored token is not proof of a session: it may have expired while the tab was closed, or
  // been revoked by a refresh-token reuse elsewhere (FR-A1). Ask the server who we are.
  useEffect(() => {
    let cancelled = false

    if (!api.isSignedIn()) {
      setLoading(false)
      return
    }
    api
      .me()
      .then((me) => {
        if (!cancelled) setUser(me)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // The client signs out on its own when a refresh is rejected. Clearing the cache matters:
  // leaving one user's portfolio in memory would show it to the next person to sign in.
  useEffect(
    () =>
      onSignedOut(() => {
        setUser(null)
        queryClient.clear()
      }),
    [queryClient],
  )

  // protectedRoute's beforeLoad only runs on a fresh navigation, so a token rejected mid-session
  // - or a sign-in completing - would otherwise leave the wrong screen rendered until the reader
  // happened to click something. Invalidating here rather than inline after each `setUser` call
  // is what makes the ordering correct: `RouterProvider` only pushes the new `auth` into the
  // router's own context during ITS render, which React has not necessarily run yet at the
  // point `setUser` was called - an effect keyed on `user` is guaranteed to run after that
  // render has committed, so the router is never invalidated against a context it has not
  // received the update for yet.
  useEffect(() => {
    void router.invalidate()
  }, [user])

  const signIn = useCallback(async (username: string, password: string) => {
    setUser(await api.login(username, password))
  }, [])

  const signOut = useCallback(async () => {
    await api.logout()
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo(
    () => ({ user, features: user?.features ?? NO_FEATURES, loading, signIn, signOut }),
    [user, loading, signIn, signOut],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthState {
  const context = use(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider')
  }
  return context
}
